import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import PageShell from "../../components/layout/PageShell";
import {
    getPlatformSettingsByPrefix,
    savePlatformSettings
} from "../../services/crmService";
import { getSetting, toSettingPayload } from "./settingsUtils";

import "../Setting.css";

export default function AdminAiSettingsPage() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState({
        defaultRoutingProfile: "Production-Balanced",
        primaryProvider: "OpenRouter",
        primaryModel: "deepseek/deepseek-chat-v3.1",
        fallbackProvider: "Groq",
        fallbackModel: "llama-3.3-70b-versatile",
        maxRetries: 2,
        timeoutSeconds: 90,
        jsonStrictMode: true,

        heroProvider: "OpenAI",
        galleryProvider: "Stability",
        logoProvider: "Ideogram",
        imageFallbackProvider: "Gemini",
        uploadToBlob: true
    });

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            try {
                setLoading(true);

                const aiSettings = await getPlatformSettingsByPrefix("ai.");
                const imageSettings = await getPlatformSettingsByPrefix("image.");
                const settings = [...(aiSettings || []), ...(imageSettings || [])];

                if (!mounted) return;

                setForm({
                    defaultRoutingProfile: getSetting(settings, "ai.defaultRoutingProfile", "Production-Balanced"),
                    primaryProvider: getSetting(settings, "ai.text.primaryProvider", "OpenRouter"),
                    primaryModel: getSetting(settings, "ai.text.primaryModel", "deepseek/deepseek-chat-v3.1"),
                    fallbackProvider: getSetting(settings, "ai.text.fallbackProvider", "Groq"),
                    fallbackModel: getSetting(settings, "ai.text.fallbackModel", "llama-3.3-70b-versatile"),
                    maxRetries: getSetting(settings, "ai.maxRetries", 2),
                    timeoutSeconds: getSetting(settings, "ai.timeoutSeconds", 90),
                    jsonStrictMode: getSetting(settings, "ai.jsonStrictMode", true),

                    heroProvider: getSetting(settings, "image.heroProvider", "OpenAI"),
                    galleryProvider: getSetting(settings, "image.galleryProvider", "Stability"),
                    logoProvider: getSetting(settings, "image.logoProvider", "Ideogram"),
                    imageFallbackProvider: getSetting(settings, "image.fallbackProvider", "Gemini"),
                    uploadToBlob: getSetting(settings, "image.uploadToBlob", true)
                });
            } catch (error) {
                console.error(error);
                toast.error("Failed to load AI settings.");
            } finally {
                if (mounted) setLoading(false);
            }
        };

        load();

        return () => {
            mounted = false;
        };
    }, []);

    const update = (key, value) => {
        setForm(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);

            await savePlatformSettings([
                toSettingPayload("ai.defaultRoutingProfile", form.defaultRoutingProfile, "string"),
                toSettingPayload("ai.text.primaryProvider", form.primaryProvider, "string"),
                toSettingPayload("ai.text.primaryModel", form.primaryModel, "string"),
                toSettingPayload("ai.text.fallbackProvider", form.fallbackProvider, "string"),
                toSettingPayload("ai.text.fallbackModel", form.fallbackModel, "string"),
                toSettingPayload("ai.maxRetries", Number(form.maxRetries || 0), "number"),
                toSettingPayload("ai.timeoutSeconds", Number(form.timeoutSeconds || 0), "number"),
                toSettingPayload("ai.jsonStrictMode", Boolean(form.jsonStrictMode), "bool"),

                toSettingPayload("image.heroProvider", form.heroProvider, "string"),
                toSettingPayload("image.galleryProvider", form.galleryProvider, "string"),
                toSettingPayload("image.logoProvider", form.logoProvider, "string"),
                toSettingPayload("image.fallbackProvider", form.imageFallbackProvider, "string"),
                toSettingPayload("image.uploadToBlob", Boolean(form.uploadToBlob), "bool")
            ]);

            toast.success("AI settings saved.");
        } catch (error) {
            console.error(error);
            toast.error(error?.response?.data || "Failed to save AI settings.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <PageShell title="AI & Models Settings">
                <div className="project-loading-card">Loading AI settings...</div>
            </PageShell>
        );
    }

    return (
        <PageShell title="AI & Models Settings">
            <div className="settings-shell">
                <section className="project-hero-card">
                    <div>
                        <span className="project-kicker">Platform Settings</span>
                        <h1>AI & Models</h1>
                        <p>
                            Configure providers, model names, routing profiles, fallback behavior,
                            retry limits, and timeout behavior.
                        </p>
                    </div>

                    <button
                        type="button"
                        className="settings-save-btn"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save Settings"}
                    </button>
                </section>

                <section className="settings-form-grid">
                    <div className="project-panel">
                        <span className="project-kicker">Text AI</span>
                        <h2>Primary Routing</h2>

                        <div className="settings-field-grid">
                            <label>
                                <span>Default Routing Profile</span>
                                <select
                                    value={form.defaultRoutingProfile}
                                    onChange={(e) => update("defaultRoutingProfile", e.target.value)}
                                >
                                    <option value="Production-Balanced">Production-Balanced</option>
                                    <option value="Production-QualityFirst">Production-QualityFirst</option>
                                    <option value="Low-Cost">Low-Cost</option>
                                </select>
                            </label>

                            <label>
                                <span>Primary Provider</span>
                                <select
                                    value={form.primaryProvider}
                                    onChange={(e) => update("primaryProvider", e.target.value)}
                                >
                                    <option value="OpenRouter">OpenRouter</option>
                                    <option value="Groq">Groq</option>
                                    <option value="Gemini">Gemini</option>
                                    <option value="Claude">Claude</option>
                                </select>
                            </label>

                            <label>
                                <span>Primary Model</span>
                                <input
                                    value={form.primaryModel}
                                    onChange={(e) => update("primaryModel", e.target.value)}
                                />
                            </label>

                            <label>
                                <span>Fallback Provider</span>
                                <select
                                    value={form.fallbackProvider}
                                    onChange={(e) => update("fallbackProvider", e.target.value)}
                                >
                                    <option value="Groq">Groq</option>
                                    <option value="OpenRouter">OpenRouter</option>
                                    <option value="Gemini">Gemini</option>
                                </select>
                            </label>

                            <label>
                                <span>Fallback Model</span>
                                <input
                                    value={form.fallbackModel}
                                    onChange={(e) => update("fallbackModel", e.target.value)}
                                />
                            </label>
                        </div>
                    </div>

                    <div className="project-panel">
                        <span className="project-kicker">Runtime</span>
                        <h2>Retries & Timeouts</h2>

                        <div className="settings-field-grid">
                            <label>
                                <span>Max Retries</span>
                                <input
                                    type="number"
                                    value={form.maxRetries}
                                    onChange={(e) => update("maxRetries", e.target.value)}
                                />
                            </label>

                            <label>
                                <span>Timeout Seconds</span>
                                <input
                                    type="number"
                                    value={form.timeoutSeconds}
                                    onChange={(e) => update("timeoutSeconds", e.target.value)}
                                />
                            </label>

                            <label>
                                <span>JSON Strict Mode</span>
                                <select
                                    value={String(form.jsonStrictMode)}
                                    onChange={(e) => update("jsonStrictMode", e.target.value === "true")}
                                >
                                    <option value="true">Enabled</option>
                                    <option value="false">Disabled</option>
                                </select>
                            </label>
                        </div>
                    </div>

                    <div className="project-panel">
                        <span className="project-kicker">Image AI</span>
                        <h2>Image Provider Routing</h2>

                        <div className="settings-field-grid">
                            <label>
                                <span>Hero Images</span>
                                <input value={form.heroProvider} onChange={(e) => update("heroProvider", e.target.value)} />
                            </label>

                            <label>
                                <span>Gallery Images</span>
                                <input value={form.galleryProvider} onChange={(e) => update("galleryProvider", e.target.value)} />
                            </label>

                            <label>
                                <span>Logo / Branding</span>
                                <input value={form.logoProvider} onChange={(e) => update("logoProvider", e.target.value)} />
                            </label>

                            <label>
                                <span>Fallback Image Provider</span>
                                <input value={form.imageFallbackProvider} onChange={(e) => update("imageFallbackProvider", e.target.value)} />
                            </label>
                        </div>

                        <div className="settings-toggle-list">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={form.uploadToBlob}
                                    onChange={(e) => update("uploadToBlob", e.target.checked)}
                                />
                                <span>Upload generated images to Azure Blob</span>
                            </label>
                        </div>
                    </div>
                </section>
            </div>
        </PageShell>
    );
}